import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import {
  getUser,
  createUser,
  updateUser,
  resetUserPassword,
  getRoles,
  assignRole,
  removeRole,
  getTracks,
  getSanctioningBodies,
  assignTrackToUser,
  removeTrackFromUser,
  assignSanctioningBodyToUser,
  removeSanctioningBodyFromUser,
} from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import { features } from '../../config/features';
import AssignmentList from '../../components/admin/AssignmentList';
import type { Role, SanctioningBody, Track, UserWithRoles } from '../../types';
import {
  PageHeader,
  Alert,
  Button,
  Field,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Spinner,
  Switch,
  cn,
} from '@racing/ui';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-4">
    <h2 className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
      {title}
    </h2>
    {children}
  </section>
);

const UserDetailPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  /**
    * The `/admin/users/new` route declares no `:userId`, so the param is
    * undefined there — NOT the string "new". Comparing against "new" makes
    * `isNew` permanently false and sends the create flow straight back to the
    * list via the invalid-id guard below.
    */
  const isNew = !userId;
  const parsedUserId = userId ? Number(userId) : 0;
  const isInvalidId = !isNew && !Number.isInteger(parsedUserId);

  const [user, setUser] = useState<Partial<UserWithRoles>>({ is_active: true });
  const [password, setPassword] = useState('');
  // Defaults to 'local' when Entra sign-in is off for this build (features.entraAuth):
  // on such a build an administrator creating a user would otherwise silently get
  // an Entra account that cannot sign in, cannot have its password reset, and (no
  // conversion feature exists yet) cannot be repaired short of hand-written SQL.
  const [authProvider, setAuthProvider] = useState<'local' | 'entra'>(
    features.entraAuth ? 'entra' : 'local'
  );
  const [entraUpn, setEntraUpn] = useState('');
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [allTracks, setAllTracks] = useState<Track[]>([]);
  const [allBodies, setAllBodies] = useState<SanctioningBody[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadReference = useCallback(async () => {
    try {
      const [roles, tracks, bodies] = await Promise.all([
        getRoles(),
        getTracks(),
        getSanctioningBodies(),
      ]);
      setAllRoles(roles);
      setAllTracks(tracks);
      setAllBodies(bodies);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }, []);

  const loadUser = useCallback(async () => {
    if (isNew || isInvalidId) return;
    try {
      setLoading(true);
      setUser(await getUser(parsedUserId));
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isNew, isInvalidId, parsedUserId]);

  useEffect(() => {
    loadReference();
    loadUser();
  }, [loadReference, loadUser]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        if (!user.username) {
          setError('Username is required');
          return;
        }
        if (authProvider === 'local' && !password) {
          setError('A password is required for a local account');
          return;
        }
        if (authProvider === 'entra' && !entraUpn) {
          setError('A work email address is required for an Entra account');
          return;
        }

        const created = await createUser({
          ...user,
          auth_provider: authProvider,
          ...(authProvider === 'local' ? { password } : { entra_upn: entraUpn }),
        } as never);
        setSuccess('User created');
        navigate(`/admin/users/${created.id}`, { replace: true });
      } else {
        setUser(await updateUser(parsedUserId, user));
        setSuccess('User saved');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      await resetUserPassword(parsedUserId, password);
      setPassword('');
      setSuccess('Password reset');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const handleToggleRole = async (role: Role) => {
    try {
      const has = user.roles?.some((r) => r.role_id === role.role_id);
      setUser(
        has
          ? await removeRole(parsedUserId, role.role_id)
          : await assignRole(parsedUserId, role.role_id)
      );
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const hasTrackOperator = user.roles?.some((r) => r.role_name === 'TRACK_OPERATOR');
  const hasSanctioningBody = user.roles?.some((r) => r.role_name === 'SANCTIONING_BODY');

  const availableTracks = useMemo(() => {
    const assigned = new Set(user.track_assignments?.map((t) => t.track_id) ?? []);
    return allTracks.filter((t) => !assigned.has(t.track_id));
  }, [allTracks, user.track_assignments]);

  const availableBodies = useMemo(() => {
    const assigned = new Set(
      user.sanctioning_body_assignments?.map((sb) => sb.sanctioning_body_id) ?? []
    );
    return allBodies.filter((sb) => !assigned.has(sb.sanctioning_body_id));
  }, [allBodies, user.sanctioning_body_assignments]);

  if (isInvalidId) return <Navigate to="/admin/users" replace />;

  if (loading) {
    return (
      <div className="flex justify-center pt-20">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col">
      <Button
        variant="ghost"
        size="sm"
        className="mb-2 -ml-2 self-start"
        onClick={() => navigate('/admin/users')}
      >
        <ArrowLeft /> Back to managed users
      </Button>

      <PageHeader
        title={isNew ? 'New Managed User' : user.display_name || user.username || 'Managed User'}
        subtitle={
          isNew
            ? undefined
            : 'Roles decide what this account can do; assignments decide which events it can reach.'
        }
        actions={
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Spinner className="size-4 text-white" label={null} /> : <Save />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <div className="flex flex-col gap-6">
        <Section title="Managed user information">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label="Username"
              required
              hint={isNew ? undefined : 'Usernames cannot be changed after creation'}
            >
              {(p) => (
                <Input
                  value={user.username || ''}
                  onChange={(e) => setUser({ ...user, username: e.target.value })}
                  disabled={!isNew}
                  autoComplete="off"
                  {...p}
                />
              )}
            </Field>
            <Field label="Display Name">
              {(p) => (
                <Input
                  value={user.display_name || ''}
                  onChange={(e) => setUser({ ...user, display_name: e.target.value })}
                  {...p}
                />
              )}
            </Field>
            <Field label="Email">
              {(p) => (
                <Input
                  type="email"
                  value={user.email || ''}
                  onChange={(e) => setUser({ ...user, email: e.target.value })}
                  {...p}
                />
              )}
            </Field>
            {isNew && (
              <Field
                label="Sign-in method"
                required
                hint="Entra accounts have no password. This cannot be changed after creation."
              >
                {(p) => (
                  <Select
                    value={authProvider}
                    onValueChange={(value) => setAuthProvider(value as 'local' | 'entra')}
                  >
                    <SelectTrigger id={p.id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entra">Microsoft Entra</SelectItem>
                      <SelectItem value="local">Local password (break-glass)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}

            {isNew && authProvider === 'entra' && (
              <Field
                label="Work email address"
                required
                hint="The person's real work email, as invited to the tenant — not the guest #EXT# form shown in the Azure portal."
              >
                {(p) => (
                  <Input
                    type="email"
                    value={entraUpn}
                    onChange={(event) => setEntraUpn(event.target.value)}
                    autoComplete="email"
                    {...p}
                  />
                )}
              </Field>
            )}

            {!isNew && (
              <Field label="Sign-in method">
                {() => (
                  <p className="font-sans text-sm text-ink-soft">
                    {user.auth_provider === 'entra'
                      ? `Microsoft Entra${user.entra_upn ? ` (${user.entra_upn})` : ''}`
                      : 'Local password'}
                  </p>
                )}
              </Field>
            )}

            {!isNew && (
              <div className="flex items-center gap-3">
                <Switch
                  id="user-active"
                  checked={user.is_active !== false}
                  onCheckedChange={(checked) => setUser({ ...user, is_active: checked })}
                />
                <Label htmlFor="user-active">Active</Label>
              </div>
            )}
          </div>
        </Section>

        <Separator />

        {(isNew ? authProvider === 'local' : user.auth_provider === 'local') && (
          <Section title={isNew ? 'Password' : 'Reset password'}>
            <div className="flex flex-wrap items-end gap-3">
              <Field
                label={isNew ? 'Password' : 'New password'}
                required={isNew}
                hint="Minimum 6 characters"
                className="min-w-[16rem] flex-1 sm:max-w-sm"
              >
                {(p) => (
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    {...p}
                  />
                )}
              </Field>
              {!isNew && (
                <Button variant="outline" onClick={handleResetPassword} disabled={!password}>
                  Reset password
                </Button>
              )}
            </div>
          </Section>
        )}

        {!isNew && (
          <>
            <Separator />

            <Section title="Roles">
              <div className="flex flex-wrap gap-2">
                {allRoles.map((role) => {
                  const has = !!user.roles?.some((r) => r.role_id === role.role_id);
                  return (
                    <button
                      key={role.role_id}
                      type="button"
                      aria-pressed={has}
                      onClick={() => handleToggleRole(role)}
                      className={cn(
                        'rounded-md border px-3 py-1.5 font-sans text-sm font-semibold outline-none transition-colors',
                        'focus-visible:ring-2 focus-visible:ring-brand',
                        has
                          ? 'border-brand bg-brand text-white hover:bg-brand-deep'
                          : 'border-line-strong bg-surface text-ink-soft hover:bg-surface-sunk'
                      )}
                    >
                      {role.role_name.replace(/_/g, ' ')}
                    </button>
                  );
                })}
              </div>
              <p className="font-sans text-xs text-ink-faint">
                Changes take effect on this user's next request — the server re-reads roles per
                request rather than trusting the token.
              </p>
            </Section>

            {hasTrackOperator && (
              <>
                <Separator />
                <Section title="Track assignments">
                  <AssignmentList
                    items={(user.track_assignments ?? []).map((track) => ({
                      id: track.track_id,
                      primary: track.track_name,
                      secondary: [track.city, track.state].filter(Boolean).join(', '),
                    }))}
                    options={availableTracks.map((track) => ({
                      id: track.track_id,
                      label: track.track_name,
                    }))}
                    addLabel="Assign track"
                    emptyMessage="No tracks assigned. This operator cannot reach any events yet."
                    onAdd={async (trackId) => {
                      try {
                        setUser(await assignTrackToUser(parsedUserId, trackId));
                      } catch (err: unknown) {
                        setError(getErrorMessage(err));
                      }
                    }}
                    onRemove={async (trackId) => {
                      try {
                        setUser(await removeTrackFromUser(parsedUserId, trackId));
                      } catch (err: unknown) {
                        setError(getErrorMessage(err));
                      }
                    }}
                  />
                </Section>
              </>
            )}

            {hasSanctioningBody && (
              <>
                <Separator />
                <Section title="Sanctioning body assignments">
                  <AssignmentList
                    items={(user.sanctioning_body_assignments ?? []).map((body) => ({
                      id: body.sanctioning_body_id,
                      primary: body.body_name,
                      secondary: body.abbreviation,
                    }))}
                    options={availableBodies.map((body) => ({
                      id: body.sanctioning_body_id,
                      label: body.body_name,
                    }))}
                    addLabel="Assign sanctioning body"
                    emptyMessage="No sanctioning bodies assigned. This user cannot reach any events yet."
                    onAdd={async (sbId) => {
                      try {
                        setUser(await assignSanctioningBodyToUser(parsedUserId, sbId));
                      } catch (err: unknown) {
                        setError(getErrorMessage(err));
                      }
                    }}
                    onRemove={async (sbId) => {
                      try {
                        setUser(await removeSanctioningBodyFromUser(parsedUserId, sbId));
                      } catch (err: unknown) {
                        setError(getErrorMessage(err));
                      }
                    }}
                  />
                </Section>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserDetailPage;
